import axios, { AxiosError, AxiosInstance } from 'axios';
import { logSuccess, logInfo } from '../utils/logger';
import { handleLogout } from '../feature-module/components/auth/apiAuth';
import {
  getPersistedSessionId,
  getPersistedStatusId,
  getPersistedActiveBusinessId,
} from '../utils/sessionHeaderStorage';

// --- CONFIG & CONSTANTS ---
const BASE_URL = import.meta.env.VITE_APP_BACKEND_PORT ?? '';
// const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 5000;
const API_TIMEOUT = 50000;
const SENSITIVE_FIELDS = ['password', 'token', 'secret', 'creditCard', 'cvv'];
const RETRYABLE_STATUS = [408, 429, 502, 503, 504];

/**
 * Multi-business support (Phase 1) — per approved_architecture.md §4.3.
 *
 * Business-scoped URL path prefixes. Requests to these paths MUST carry an
 * `X-Active-Business-Id` header. The active business ID is read from
 * sessionStorage (set by the business switcher or auth response).
 */
const BUSINESS_SCOPED_PATH_PREFIXES = [
  '/provider/',
  '/providers/',
  '/business/',
  '/v1/business/',
  '/admin/',
];

const getActiveBusinessId = (): string | null => {
  return getPersistedActiveBusinessId();
};

// --- STATE ---
let isRefreshing = false;
let pendingQueue: Array<{
  resolve: (value?: any) => void;
  reject: (err: any) => void;
}> = [];

// --- UTILS ---
const sanitize = (data: any): any => {
  if (!data || typeof data !== 'object') return data;
  const clone = JSON.parse(JSON.stringify(data));
  const redact = (obj: any) => {
    Object.keys(obj).forEach((key) => {
      if (SENSITIVE_FIELDS.includes(key.toLowerCase())) obj[key] = '[REDACTED]';
      else if (obj[key] && typeof obj[key] === 'object') redact(obj[key]);
    });
  };
  redact(clone);
  return clone;
};

const redirectToLogout = () => {
  sessionStorage.clear();
  handleLogout();
};

// --- INSTANCE ---
export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: API_TIMEOUT,
  withCredentials: true,
});

// --- INTERCEPTORS ---

apiClient.interceptors.request.use((config) => {
  config.headers['x-client-portal'] = 'customer';
  config.headers['x-active-role'] = 'customer';
  const persistedSessionId = getPersistedSessionId();
  const persistedStatusId = getPersistedStatusId();
  if (persistedSessionId) {
    config.headers['x-session-id'] = persistedSessionId;
  }
  if (persistedStatusId) {
    config.headers['x-session-status-id'] = persistedStatusId;
  }

  // Multi-business support (Phase 1) — per approved_architecture.md §4.3
  // Attach X-Active-Business-Id header to business-scoped requests.
  // The backend requireBusinessScope guard validates this against JWT businessIds[].
  const url = config.url ?? '';
  const isBusinessScoped = BUSINESS_SCOPED_PATH_PREFIXES.some((prefix) =>
    url.startsWith(prefix)
  );
  if (isBusinessScoped) {
    const activeBusinessId = getActiveBusinessId();
    if (activeBusinessId) {
      config.headers['x-active-business-id'] = activeBusinessId;
    }
  }

  logInfo(`🚀 [${config.method?.toUpperCase()}] ${config.url}`, {
    params: sanitize(config.params),
    data: sanitize(config.data),
  });
  return config;
});

apiClient.interceptors.response.use(
  (res) => (logSuccess(`✅ ${res.config.url}`), res),
  async (error: AxiosError<any>) => {
    const originalReq = error.config as any;
    const status = error.response?.status;
    const errorData = error.response?.data;

    if (!originalReq) return Promise.reject(error);

    // 1. Handle 401 & Token Refresh
    //
    // Refresh on any 401 EXCEPT the ones that mean the session itself is gone.
    //
    // The old list matched TOKEN_EXPIRE and NO_SESSION — but the backend never
    // emits NO_SESSION at all, so in practice only TOKEN_EXPIRE ever triggered
    // a refresh. Every other stale-token code the services return fell through
    // as a page error, which is why a browser reload "fixed" it: a reload
    // re-runs /auth/me, the only endpoint that silently renews the access token
    // held in the session. Matching liberally covers the shared auth middleware
    // too, which can emit codes this client has never seen.
    //
    // But a refresh needs a session to refresh FROM, so SESSION_GONE_CODES must
    // stay excluded. This is a public site: an anonymous visitor on the landing
    // page gets a perfectly normal 401 INVALID_SESSION from /auth/me. Trying to
    // refresh that fails, and the failure path force-logs-out, which loops:
    //   /auth/me 401 -> refresh 401 -> logout -> Cognito -> landing -> repeat
    const SESSION_GONE_CODES = [
      'INVALID_SESSION',
      'SESSION_EXPIRED',
      'FORCE_LOGOUT',
      'TOKEN_EXPIRE',
      'NO_SESSION',
    ];

    const isRefreshCall = originalReq.url?.includes('/auth/refresh');
    const isSessionGone = SESSION_GONE_CODES.includes(errorData?.code);

    if (
      status === 401 &&
      !isSessionGone &&
      !originalReq._retry &&
      !isRefreshCall
    ) {
      // Mark before queueing, not just on the request that wins the race, so a
      // queued request that 401s again logs out instead of refreshing anew.
      originalReq._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject });
        }).then(() => apiClient(originalReq));
      }

      isRefreshing = true;
      try {
        await axios.get(
          `${BASE_URL}/user/api/v1/auth/refresh?portal=customer&client=web`,
          {
            withCredentials: true,
          }
        );
        pendingQueue.forEach((q) => q.resolve());
        pendingQueue = [];
        return apiClient(originalReq);
      } catch (err) {
        // Previously the queue was never drained on failure, so every request
        // waiting behind a failed refresh hung forever on a pending promise.
        pendingQueue.forEach((q) => q.reject(err));
        pendingQueue = [];
        redirectToLogout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // 2. Handle Retries for Transient Errors (502, 503, 504, 429)
    if (
      RETRYABLE_STATUS.includes(status!) &&
      (originalReq._retryCount ?? 0) < 2
    ) {
      originalReq._retryCount = (originalReq._retryCount ?? 0) + 1;
      await new Promise((res) =>
        setTimeout(res, 300 * originalReq._retryCount)
      );
      return apiClient(originalReq);
    }
    return Promise.reject(error);
  }
);

// --- CONVENIENCE HELPERS ---
export const http = {
  get: <T>(url: string) => apiClient.get<T>(url).then((r) => r.data),
  post: <T>(url: string, data?: any) =>
    apiClient.post<T>(url, data).then((r) => r.data),
  put: <T>(url: string, data?: any) =>
    apiClient.put<T>(url, data).then((r) => r.data),
  delete: <T>(url: string) => apiClient.delete<T>(url).then((r) => r.data),
};
